import { Component } from '@theme/component';
import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';
import { CTA_ADD, CTA_CHECKOUT, nextCheckoutCta, setButtonLabel, submitCheckout } from '@theme/wc-checkout-cta';
import { promptSizeChoice } from '@theme/wc-size-gate';

export const STICKY_SHOW_PX = -24;
export const STICKY_HIDE_PX = 16;

/**
 * @param {boolean} isStuck
 * @param {number} buttonBottom
 * @returns {boolean}
 */
export function nextStickyStuck(isStuck, buttonBottom) {
  if (!isStuck && buttonBottom <= STICKY_SHOW_PX) return true;
  if (isStuck && buttonBottom >= STICKY_HIDE_PX) return false;
  return Boolean(isStuck);
}

export const STICKY_GATE_CHOOSE_SIZE = 'choose-size';
export const STICKY_GATE_ADD = 'add';
export const STICKY_GATE_SOLD_OUT = 'sold-out';
export const STICKY_GATE_UNAVAILABLE = 'unavailable';

/**
 * Mobile only: show when the buy cluster is fully above or fully below the viewport.
 * @param {boolean} isStuck
 * @param {{ top: number, bottom: number } | null | undefined} cluster
 * @param {number} viewportHeight
 * @returns {boolean}
 */
export function nextMobileStickyVisible(isStuck, cluster, viewportHeight) {
  if (!cluster) return false;
  const clearedAbove = cluster.bottom <= STICKY_SHOW_PX;
  const canUseBelow = viewportHeight > 0;
  const belowShowEdge = viewportHeight - STICKY_SHOW_PX;
  const clearedBelow = canUseBelow && cluster.top >= belowShowEdge;
  if (!isStuck) return clearedAbove || clearedBelow;
  if (clearedAbove || clearedBelow) return true;
  return !clusterReentered(cluster, viewportHeight, canUseBelow);
}

/**
 * @param {{ top: number, bottom: number }} cluster
 * @param {number} viewportHeight
 * @param {boolean} canUseBelow
 * @returns {boolean}
 */
function clusterReentered(cluster, viewportHeight, canUseBelow) {
  const enteredFromAbove = cluster.bottom >= STICKY_HIDE_PX;
  if (!canUseBelow) return enteredFromAbove;
  const enteredFromBelow = cluster.top <= viewportHeight - STICKY_HIDE_PX;
  return enteredFromAbove && enteredFromBelow;
}

/**
 * The picker's `data-wc-size-chosen` is the only size-choice signal. A URL variant is not one.
 * @param {string | null | undefined} pickerValue
 * @param {string | null | undefined} ownValue
 * @param {boolean} hasSizeOption
 * @returns {boolean}
 */
export function stickySizeChosen(pickerValue, ownValue, hasSizeOption) {
  const raw = pickerValue === 'true' || pickerValue === 'false' ? pickerValue : ownValue;
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return hasSizeOption !== true;
}

/**
 * @param {boolean} chosen
 * @param {string | boolean | null | undefined} variantAvailable
 * @returns {string}
 */
export function stickyPurchaseGate(chosen, variantAvailable) {
  if (!chosen) return STICKY_GATE_CHOOSE_SIZE;
  if (variantAvailable === true || variantAvailable === 'true') return STICKY_GATE_ADD;
  if (variantAvailable == null || variantAvailable === '') return STICKY_GATE_UNAVAILABLE;
  return STICKY_GATE_SOLD_OUT;
}

/**
 * @param {ParentNode | null | undefined} picker
 * @returns {Element | null}
 */
export function sizePickerFocusTarget(picker) {
  if (!picker || typeof picker.querySelector !== 'function') return null;
  const field = picker.querySelector('[data-option-name="size"]');
  const checked = field?.querySelector('input:checked');
  return checked || picker;
}

/**
 * @param {Element | null | undefined} addButton
 * @param {Iterable<Element> | null | undefined} buyNowControls
 * @returns {{ top: number, bottom: number } | null}
 */
export function clusterBounds(addButton, buyNowControls) {
  const boxes = [];
  const addBox = elementBox(addButton);
  if (addBox) boxes.push(addBox);
  for (const control of buyNowControls ?? []) {
    const box = elementBox(control);
    if (box) boxes.push(box);
  }
  if (!boxes.length) return null;
  return unionBoxes(boxes);
}

const BUY_NOW_SELECTOR = 'shopify-accelerated-checkout, .shopify-payment-button';

/**
 * @param {DOMRect | { width?: number, height?: number, bottom?: number } | null | undefined} rect
 * @returns {boolean}
 */
function hasNonEmptyBox(rect) {
  return Boolean(rect) && rect.width > 0 && rect.height > 0 && typeof rect.bottom === 'number';
}

/**
 * @param {Element | null | undefined} element
 * @returns {{ top: number, bottom: number } | null}
 */
function elementBox(element) {
  if (!element || typeof element.getBoundingClientRect !== 'function') return null;
  const rect = element.getBoundingClientRect();
  if (!hasNonEmptyBox(rect)) return null;
  const top = typeof rect.top === 'number' ? rect.top : rect.bottom - rect.height;
  return { top, bottom: rect.bottom };
}

/**
 * @param {{ top: number, bottom: number }[]} boxes
 * @returns {{ top: number, bottom: number }}
 */
function unionBoxes(boxes) {
  let top = boxes[0].top;
  let bottom = boxes[0].bottom;
  for (const box of boxes) {
    if (box.top < top) top = box.top;
    if (box.bottom > bottom) bottom = box.bottom;
  }
  return { top, bottom };
}

/** @returns {number} */
function readViewportHeight() {
  const height = window.innerHeight;
  return typeof height === 'number' && height > 0 ? height : 0;
}

/**
 * @param {HTMLButtonElement} button
 * @param {string} label
 */
function writeButtonLabel(button, label) {
  setButtonLabel(button, label);
}

/**
 * @param {Element | null | undefined} element
 * @returns {number | null}
 */
function visibleBottom(element) {
  if (!element || typeof element.getBoundingClientRect !== 'function') return null;
  const rect = element.getBoundingClientRect();
  return hasNonEmptyBox(rect) ? rect.bottom : null;
}

/**
 * Visually lowest bottom in the buy cluster. Larger values sit further down.
 * Missing controls and empty boxes are ignored.
 * @param {Element | null | undefined} addButton
 * @param {Iterable<Element> | null | undefined} buyNowControls
 * @returns {number | null}
 */
export function buyClusterBottom(addButton, buyNowControls) {
  const bottoms = [visibleBottom(addButton)];
  for (const control of buyNowControls ?? []) bottoms.push(visibleBottom(control));
  const present = bottoms.filter((bottom) => bottom != null);
  if (!present.length) return null;
  return Math.max(...present);
}

/**
 * @param {ParentNode | null | undefined} root
 * @returns {Element[]}
 */
function buyNowIn(root) {
  if (!root || typeof root.querySelectorAll !== 'function') return [];
  const found = root.querySelectorAll(BUY_NOW_SELECTOR);
  if (!found) return [];
  const controls = [];
  for (const el of found) {
    controls.push(el);
    if (el.shadowRoot) controls.push(...buyNowIn(el.shadowRoot));
  }
  return controls;
}

/**
 * @param {Element[]} controls
 * @returns {Element[]}
 */
function uniqueElements(controls) {
  const seen = new Set();
  const unique = [];
  for (const el of controls) {
    if (!el || seen.has(el)) continue;
    seen.add(el);
    unique.push(el);
  }
  return unique;
}

/**
 * @param {Element | null | undefined} body
 * @returns {boolean}
 */
export function isBodyScrollLocked(body) {
  if (!body) return false;
  const view = body.ownerDocument?.defaultView;
  return view?.getComputedStyle(body).position === 'fixed';
}

/**
 * @param {Document} root
 * @returns {boolean}
 */
export function isStickyOverlayActive(root) {
  if (root.querySelector('dialog[open]')) return true;
  if (root.documentElement?.hasAttribute('scroll-lock')) return true;
  if (root.querySelector('header-drawer details[open]')) return true;
  return isBodyScrollLocked(root.body);
}

const NON_TEXT_INPUT_TYPES = new Set(['radio', 'checkbox', 'button', 'submit', 'reset', 'range', 'color', 'file', 'image']);

/**
 * True only while a text-entry control (which opens the on-screen keyboard) has focus.
 * @param {Document} root
 * @returns {boolean}
 */
export function isStickyKeyboardActive(root) {
  const active = root.activeElement;
  if (active?.tagName === 'TEXTAREA') return true;
  if (active?.tagName !== 'INPUT') return false;
  const type = String(active.type || 'text').toLowerCase();
  return !NON_TEXT_INPUT_TYPES.has(type);
}

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when add to cart and buy now have scrolled out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {AbortController | null} */
  #mobileAbortController = null;

  /** @type {AbortController | null} */
  #desktopAbortController = null;

  /** @type {number | null} */
  #rafId = null;

  #mobileFramePending = false;

  #desktopFramePending = false;

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {boolean} */
  #addInFlight = false;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #sizeChosen = false;

  connectedCallback() {
    super.connectedCallback();
    this.#sizeChosen = this.#resolveSizeChosen();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener('wc:size-restored', this.syncRememberedSize, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange, { signal });
    this.#connectViewportObservers();
    this.#getInitialQuantity();
    this.#applySizeGate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#disconnectViewportObservers();
    this.#abortController.abort();
    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
  }

  #handleBreakpointChange = () => {
    this.#disconnectViewportObservers();
    this.#connectViewportObservers();
  };

  #connectViewportObservers() {
    if (isMobileBreakpoint()) {
      this.#connectMobileVisibility();
      return;
    }
    this.#connectDesktopVisibility();
  }

  #disconnectViewportObservers() {
    this.#mobileAbortController?.abort();
    this.#mobileAbortController = null;
    this.#desktopAbortController?.abort();
    this.#desktopAbortController = null;
    if (this.#rafId != null) cancelAnimationFrame(this.#rafId);
    this.#rafId = null;
    this.#mobileFramePending = false;
    this.#desktopFramePending = false;
  }

  #connectMobileVisibility() {
    this.#mobileAbortController = new AbortController();
    const { signal } = this.#mobileAbortController;
    const opts = { signal, passive: true };
    window.addEventListener('scroll', this.#scheduleMobileUpdate, opts);
    window.addEventListener('resize', this.#scheduleMobileUpdate, opts);
    document.addEventListener(DialogOpenEvent.eventName, this.#scheduleMobileUpdate, { capture: true, signal });
    document.addEventListener(DialogCloseEvent.eventName, this.#scheduleMobileUpdate, { capture: true, signal });
    document.addEventListener('toggle', this.#scheduleMobileUpdate, { capture: true, signal });
    document.addEventListener('focusin', this.#scheduleMobileUpdate, { signal });
    document.addEventListener('focusout', this.#scheduleMobileUpdate, { signal });
    this.#reacquireBuyButton();
    this.#scheduleMobileUpdate();
  }

  #scheduleMobileUpdate = () => {
    if (this.#mobileFramePending) return;
    this.#mobileFramePending = true;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#mobileFramePending = false;
      this.#updateMobileVisibility();
    });
  };

  #updateMobileVisibility = () => {
    if (!this.#mobileAbortController || this.#mobileAbortController.signal.aborted) return;
    if (!isMobileBreakpoint()) return;
    if (isStickyOverlayActive(document) || isStickyKeyboardActive(document)) {
      this.#syncBarAccess(false);
      return;
    }
    this.#applyMobileHysteresis();
  };

  #applyMobileHysteresis() {
    const button = this.#reacquireBuyButton();
    if (!button) return;
    const bounds = clusterBounds(button, this.#buyNowControls(this.#getProductForm()));
    const nextStuck = this.#nextMobileStuck(bounds, button);
    if (nextStuck) this.#showStickyBar();
    else this.#hideStickyBar();
  }

  /**
   * @param {{ top: number, bottom: number } | null} bounds
   * @param {HTMLButtonElement} button
   * @returns {boolean}
   */
  #nextMobileStuck(bounds, button) {
    if (bounds) return nextMobileStickyVisible(this.#isStuck, bounds, readViewportHeight());
    return nextStickyStuck(this.#isStuck, button.getBoundingClientRect().bottom);
  }

  #connectDesktopVisibility() {
    this.#desktopAbortController = new AbortController();
    const { signal } = this.#desktopAbortController;
    const opts = { signal, passive: true };
    window.addEventListener('scroll', this.#scheduleDesktopUpdate, opts);
    window.addEventListener('resize', this.#scheduleDesktopUpdate, opts);
    this.#reacquireBuyButton();
    this.#scheduleDesktopUpdate();
  }

  #scheduleDesktopUpdate = () => {
    if (!this.#desktopAbortController || this.#desktopAbortController.signal.aborted) return;
    if (this.#desktopFramePending) return;
    this.#desktopFramePending = true;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#desktopFramePending = false;
      this.#updateDesktopVisibility();
    });
  };

  #updateDesktopVisibility = () => {
    if (!this.#desktopAbortController || this.#desktopAbortController.signal.aborted) return;
    if (isMobileBreakpoint()) return;
    this.#applyDesktopHysteresis();
  };

  #applyDesktopHysteresis() {
    const clusterBottom = this.#buyClusterBottom();
    if (clusterBottom == null) return;
    const nextStuck = nextStickyStuck(this.#isStuck, clusterBottom);
    if (nextStuck) this.#showStickyBar();
    else this.#hideStickyBar();
  }

  #buyClusterBottom() {
    const form = this.#getProductForm();
    return buyClusterBottom(this.#reacquireBuyButton(), this.#buyNowControls(form));
  }

  /**
   * @param {HTMLElement | null} form
   * @returns {Element[]}
   */
  #buyNowControls(form) {
    const block = form?.closest?.('.buy-buttons-block') ?? null;
    return uniqueElements([...buyNowIn(form), ...buyNowIn(block)]);
  }

  /**
   * @returns {HTMLButtonElement | null}
   */
  #reacquireBuyButton() {
    const form = this.#getProductForm();
    const button = form?.querySelector('[ref="addToCartButton"]');
    if (!button || typeof button.getBoundingClientRect !== 'function') {
      this.#targetAddToCartButton = null;
      return null;
    }
    this.#targetAddToCartButton = button;
    return button;
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (this.#revealSizePicker()) return;
    this.#reacquireBuyButton();
    if (this.#isCheckoutCta()) {
      submitCheckout(Theme.routes.cart_url);
      return;
    }
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#addInFlight = true;
    this.#setStickyLoading(true);
    this.#targetAddToCartButton.click();

    if (this.#targetAddToCartButton.dataset.loading !== 'true') {
      this.#addInFlight = false;
      this.#setStickyLoading(false);
      this.#targetAddToCartButton.dataset.puppet = 'false';
      return;
    }

    if (matchMedia('(max-width: 749px)').matches) return;

    const cartIcon = document.querySelector('.header-actions__cart-icon');
    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;
    document.body.appendChild(flyToCartElement);
  };

  /** @param {boolean} on */
  #setStickyLoading(on) {
    const button = this.refs.addToCartButton;
    if (!button) return;
    if (on) {
      button.setAttribute('data-loading', 'true');
      button.setAttribute('aria-busy', 'true');
      return;
    }
    button.removeAttribute('data-loading');
    button.removeAttribute('aria-busy');
  }

  releasePendingAdd() {
    this.#addInFlight = false;
    this.#setStickyLoading(false);
  }

  #playStickyAdded() {
    const button = this.refs.addToCartButton;
    if (!button) return;
    button.dataset.added = 'true';
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    this.#resetTimeout = setTimeout(() => {
      button.removeAttribute('data-added');
    }, 800);
  }

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;
    this.#syncSizeChosenFromPicker();
    this.#restoreChosenLabel();

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    this.#reacquireBuyButton();

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    this.#updateButtonText();
    this.#applySizeGate();
    this.#restoreChosenLabel();
    this.#scheduleMobileUpdate();
    this.#scheduleDesktopUpdate();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
    this.#syncSizeChosenFromPicker();
    this.#restoreChosenLabel();
  };

  /** @returns {boolean} */
  #resolveSizeChosen() {
    const section = this.closest('.shopify-section');
    const picker = section?.querySelector?.('variant-picker');
    const pickerValue = picker?.getAttribute?.('data-wc-size-chosen') ?? picker?.dataset?.wcSizeChosen;
    return stickySizeChosen(pickerValue, this.dataset.wcSizeChosen, this.dataset.hasSizeOption === 'true');
  }

  syncRememberedSize = () => {
    this.#sizeChosen = this.#resolveSizeChosen();
    this.dataset.wcSizeChosen = this.#sizeChosen ? 'true' : 'false';
    this.#applySizeGate();
    this.#restoreChosenLabel();
  };

  #syncSizeChosenFromPicker() {
    const section = this.closest('.shopify-section');
    const picker = typeof section?.querySelector === 'function' ? section.querySelector('variant-picker') : null;
    const raw = picker?.dataset?.wcSizeChosen ?? picker?.getAttribute?.('data-wc-size-chosen');
    if (raw !== 'true' && raw !== 'false') return;
    this.#sizeChosen = raw === 'true';
    this.dataset.wcSizeChosen = raw;
  }

  /** @returns {boolean} */
  #isCheckoutCta() {
    const stickyButton = this.refs.addToCartButton;
    return this.#ctaState(stickyButton) === CTA_CHECKOUT || this.#ctaState(this.#targetAddToCartButton) === CTA_CHECKOUT;
  }

  /** @param {HTMLButtonElement | null | undefined} button */
  #ctaState(button) {
    return button?.dataset.wcCta === CTA_CHECKOUT ? CTA_CHECKOUT : CTA_ADD;
  }

  /** @param {HTMLButtonElement} button */
  #leaveCheckout(button) {
    const next = nextCheckoutCta(this.#ctaState(button), 'variant-change');
    if (next === CTA_ADD) delete button.dataset.wcCta;
  }

  /** @param {Array<{ variant_id?: number | string, quantity?: number }> | undefined} items */
  #revertCheckoutIfRemoved(items) {
    const button = this.refs.addToCartButton;
    if (!button) return;
    const next = nextCheckoutCta(this.#ctaState(button), 'cart-update', {
      items,
      selectedVariantId: this.dataset.currentVariantId,
    });
    if (next !== CTA_ADD || button.dataset.wcCta !== CTA_CHECKOUT) return;
    delete button.dataset.wcCta;
    const gate = stickyPurchaseGate(this.#sizeChosen, this.dataset.variantAvailable);
    if (gate === STICKY_GATE_ADD) writeButtonLabel(button, this.dataset.addLabel || '');
    this.#applySizeGate();
  }

  /** @param {Array<{ variant_id?: number | string, quantity?: number }> | undefined} items */
  #enterCheckoutFromSync(items) {
    const button = this.refs.addToCartButton;
    if (!button || !this.#sizeChosen) return;
    const next = nextCheckoutCta(this.#ctaState(button), 'cart-sync', {
      items,
      selectedVariantId: this.dataset.currentVariantId,
    });
    if (next === CTA_CHECKOUT) {
      button.dataset.wcCta = CTA_CHECKOUT;
      if (button.dataset.wcGate !== STICKY_GATE_CHOOSE_SIZE) writeButtonLabel(button, this.dataset.checkoutLabel || '');
      return;
    }
    if (button.dataset.wcCta !== CTA_CHECKOUT) return;
    delete button.dataset.wcCta;
    if (button.dataset.wcGate === STICKY_GATE_ADD) writeButtonLabel(button, this.dataset.addLabel || '');
  }

  #revealSizePicker() {
    if (this.#sizeChosen) return false;
    const picker = this.closest('.shopify-section')?.querySelector('variant-picker');
    if (!picker) return true;
    promptSizeChoice(picker);
    return true;
  }

  #restoreChosenLabel() {
    const button = this.refs.addToCartButton;
    if (!button || !this.dataset.addLabel) return;
    const gate = stickyPurchaseGate(this.#sizeChosen, this.dataset.variantAvailable);
    button.dataset.wcGate = gate;
    if (gate !== STICKY_GATE_ADD) return;
    this.#leaveCheckout(button);
    writeButtonLabel(button, this.dataset.addLabel);
  }

  #applySizeGate() {
    const button = this.refs.addToCartButton;
    if (!button) return;
    const gate = stickyPurchaseGate(this.#sizeChosen, this.dataset.variantAvailable);
    button.dataset.wcGate = gate;
    if (gate !== STICKY_GATE_CHOOSE_SIZE) return;
    writeButtonLabel(button, this.dataset.chooseSizeLabel || '');
    button.disabled = false;
    if (this.refs.quantityDisplay) this.refs.quantityDisplay.style.display = 'none';
  }

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
    const failed = event.type === ThemeEvents.cartError || event.detail?.data?.didError;
    if (!failed && event.detail?.data?.source === 'wc-easysell-sync') {
      this.#enterCheckoutFromSync(event.detail?.resource?.items);
    } else if (!failed) {
      this.#revertCheckoutIfRemoved(event.detail?.resource?.items);
    }
    if (!this.#addInFlight) return;
    this.#addInFlight = false;
    this.#setStickyLoading(false);
    if (failed) return;
    this.#playStickyAdded();
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    const button = this.refs.addToCartButton;
    const next = nextCheckoutCta(this.#ctaState(button), 'quantity-change');
    if (button && next === CTA_ADD && button.dataset.wcCta === CTA_CHECKOUT) {
      this.#leaveCheckout(button);
      if (button.dataset.wcGate !== STICKY_GATE_CHOOSE_SIZE) writeButtonLabel(button, this.dataset.addLabel || '');
    }
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    this.#syncBarAccess(true);
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    this.#syncBarAccess(false);
  }

  /**
   * @param {boolean} visible
   */
  #syncBarAccess(visible) {
    const { stickyBar } = this.refs;
    if (!stickyBar) return;
    stickyBar.toggleAttribute('inert', !visible);
    stickyBar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    if (!visible) stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled && addToCartButton.dataset.wcCta !== CTA_CHECKOUT;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
